import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { BRAND, CALL_SALES } from "../lib/constants";
import QuoteDialog from "./QuoteDialog";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/projects", label: "Projects" },
  { to: "/clients", label: "Clients" },
  { to: "/software", label: "Software" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-200 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
            <img
              src={BRAND.logoUrl}
              alt="Hayat Computers and Security Solutions"
              className="h-9 md:h-11 w-auto object-contain"
            />
            <span className="sr-only">Hayat Computers and Security Solutions</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive
                      ? "text-[#1C6B8A] bg-[#E8F4F8]"
                      : "text-slate-700 hover:text-[#1C6B8A] hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={CALL_SALES}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full brand-outline-btn text-sm font-semibold"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden md:inline">{BRAND.salesDisplay}</span>
              <span className="md:hidden">Call</span>
            </a>
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="header-get-quote-btn"
              className="hidden md:inline-flex items-center h-10 px-5 rounded-full brand-btn text-sm font-semibold"
            >
              Get Quote
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-sm font-medium ${
                      isActive
                        ? "bg-[#E8F4F8] text-[#1C6B8A]"
                        : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  setQuoteOpen(true);
                }}
                data-testid="mobile-get-quote-btn"
                className="mt-2 h-11 rounded-full brand-btn font-semibold"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        )}
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </header>
  );
}
